/**
 * 文档管理HTTP API服务
 *
 * 封装文档相关的REST API调用，用于文档上传、切片、解析、查询等操作。
 * 支持 Cookie 认证（推荐）和 Authorization Header 认证。
 */

import type {
  Document,
  DocumentListQuery,
  DocumentListResponse,
  DocumentParseResponse,
  DocumentSliceRequest,
  DocumentSliceResponse,
  DocumentStatusResponse,
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentUpdate,
  KnowledgeBaseHealthResponse,
  MaterialQueryRequest,
  MaterialQueryResponse,
} from '../types/document';

// API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// HTTP错误类（复用 workspaceApi 的错误类）
export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ValidationError extends HttpError {
  constructor(message: string = '数据验证失败') {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class PayloadTooLargeError extends HttpError {
  constructor(message: string = '文件大小超过限制') {
    super(message, 413, 'PAYLOAD_TOO_LARGE');
    this.name = 'PayloadTooLargeError';
  }
}

/**
 * 获取请求头
 *
 * @param token - 可选的认证令牌，如果提供则使用 Header 认证，否则使用 Cookie 认证
 * @param isFormData - 是否为 FormData 请求（不设置 Content-Type）
 */
function getHeaders(token?: string, isFormData: boolean = false): HeadersInit {
  const headers: HeadersInit = {};
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * 处理HTTP响应错误
 */
async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析JSON，使用默认错误消息
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    case 413:
      throw new PayloadTooLargeError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

/**
 * 文档API服务类
 */
export class DocumentApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 上传文档
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param request - 上传请求参数
   * @returns 上传响应
   */
  async uploadDocument(
    token: string | undefined,
    request: DocumentUploadRequest
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('workspace_id', request.workspace_id.toString());
    if (request.materialType) {
      formData.append('material_type', request.materialType);
    }

    const response = await fetch(`${this.baseUrl}/api/documents/upload`, {
      method: 'POST',
      headers: getHeaders(token, true),
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 切片文档
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   * @param request - 切片请求参数
   * @returns 切片响应
   */
  async sliceDocument(
    token: string | undefined,
    documentId: string,
    request: DocumentSliceRequest
  ): Promise<DocumentSliceResponse> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}/slice`, {
      method: 'POST',
      headers: getHeaders(token),
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 解析文档
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   * @returns 解析响应
   */
  async parseDocument(
    token: string | undefined,
    documentId: string
  ): Promise<DocumentParseResponse> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}/parse`, {
      method: 'POST',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取文档详情（只读，不触发重新处理）
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   * @returns 文档详情响应
   */
  async getDocumentDetail(
    token: string | undefined,
    documentId: string
  ): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 更新文档内容和元数据
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   * @param request - 更新请求参数
   * @returns 文档详情响应
   */
  async updateDocument(
    token: string | undefined,
    documentId: string,
    request: DocumentUpdate
  ): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}`, {
      method: 'PATCH',
      headers: getHeaders(token),
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取文档状态
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   * @returns 文档状态响应
   */
  async getDocumentStatus(
    token: string | undefined,
    documentId: string
  ): Promise<DocumentStatusResponse> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}/status`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 列出文档
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param query - 查询参数
   * @returns 文档列表响应
   */
  async listDocuments(
    token: string | undefined,
    query: DocumentListQuery = {}
  ): Promise<DocumentListResponse> {
    const params = new URLSearchParams();
    if (query.workspace_id !== undefined) {
      params.append('workspace_id', query.workspace_id.toString());
    }
    if (query.status !== undefined) {
      params.append('status', query.status);
    }
    if (query.page !== undefined) {
      params.append('page', query.page.toString());
    }
    if (query.page_size !== undefined) {
      params.append('page_size', query.page_size.toString());
    }

    const url = params.toString()
      ? `${this.baseUrl}/api/documents?${params.toString()}`
      : `${this.baseUrl}/api/documents`;

    const response = await fetch(url, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 删除文档
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param documentId - 文档ID
   */
  async deleteDocument(token: string | undefined, documentId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/documents/${documentId}`, {
      method: 'DELETE',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }
  }

  /**
   * 查询素材库
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param request - 查询请求参数
   * @returns 查询响应
   */
  async queryMaterials(
    token: string | undefined,
    request: MaterialQueryRequest
  ): Promise<MaterialQueryResponse> {
    const response = await fetch(`${this.baseUrl}/api/materials/query`, {
      method: 'POST',
      headers: getHeaders(token),
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取素材库健康状态
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @returns 健康状态响应
   */
  async getMaterialHealth(
    token: string | undefined,
    workspaceId: number
  ): Promise<KnowledgeBaseHealthResponse> {
    const response = await fetch(`${this.baseUrl}/api/health/materials?workspace_id=${workspaceId}`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取设定库健康状态
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @returns 健康状态响应
   */
  async getSettingHealth(
    token: string | undefined,
    workspaceId: number
  ): Promise<KnowledgeBaseHealthResponse> {
    const response = await fetch(`${this.baseUrl}/api/health/settings?workspace_id=${workspaceId}`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取章节库健康状态
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @returns 健康状态响应
   */
  async getChapterHealth(
    token: string | undefined,
    workspaceId: number
  ): Promise<KnowledgeBaseHealthResponse> {
    const response = await fetch(`${this.baseUrl}/api/health/chapters?workspace_id=${workspaceId}`, {
      method: 'GET',
      headers: getHeaders(token),
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取知识库汇总健康状态
   *
   * @param token - 可选的认证令牌（Cookie 认证时不需要）
   * @param workspaceId - 工作区ID
   * @returns 健康状态响应
   */
  async getKnowledgeBaseHealth(
    token: string | undefined,
    workspaceId: number
  ): Promise<KnowledgeBaseHealthResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/health/knowledge-bases?workspace_id=${workspaceId}`,
      {
        method: 'GET',
        headers: getHeaders(token),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }
}

// 导出单例实例
export const documentApi = new DocumentApiService();

export default documentApi;
